// Split out of src/components/Tests.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). Tests.jsx
// re-exports everything it exported before, so existing imports keep working.

import { CoinIcon, Icon, OinkIcon } from "../Common.jsx";
import { OINK_CORRECT_OP } from "../../lib/oinks.js";
import { t as ct } from "../../lib/content.js";
import { useBackLayer } from "../../lib/backstack.js";

// Numeric answer check shared by TTStreaksTest and MathsWorkoutTest — accepts a little
// float slop since Maths Workout questions can have decimal answers (e.g. "12.5 - 3.2").
function answersMatch(given, answer) {
  const n = parseFloat(given);
  if (Number.isNaN(n)) return false;
  return Math.abs(n - answer) < 0.005;
}

// Tiny dependency-free sound effects via the Web Audio API — no audio files to host. A single
// shared AudioContext is reused (rather than a new one per beep) so it stays "unlocked" after
// the first user-gesture-triggered sound, including later programmatic calls like a countdown
// timer hitting zero with no tap involved.
let sharedAudioCtx = null;
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedAudioCtx) sharedAudioCtx = new Ctx();
  if (sharedAudioCtx.state === "suspended") sharedAudioCtx.resume();
  return sharedAudioCtx;
}
function beep(freq, duration, type) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type || "sine";
    osc.frequency.value = freq;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0.16, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.start(now);
    osc.stop(now + duration);
  } catch {
    // Audio is a nice-to-have here — never let a sound failure interrupt the test.
  }
}
export function playDing() { beep(880, 0.15); setTimeout(() => beep(1175, 0.18), 90); }
export function playError() { beep(220, 0.28, "square"); }

// Full-screen overlay every Learn & Earn game's active test runs inside, so a stray swipe
// can't scroll the underlying app around mid-question. Locks background scroll while mounted;
// the close (×) button and the games' own Quit/Restart controls both exit via the same
// `onClose`, returning to the normal pill-selection screen behind it.
// LB-368 (Alan, 23 Sep, option B of three drawn): while a test is RUNNING it takes the whole
// screen — tab bar hidden, Quit in a top bar — and the on-screen keyboard or number pad docks
// full width at the bottom, like the phone's own. Measured at 390pt, the keys inside the two
// nested cards were 27pt wide; docked they are ~33pt. The column never grows past the app's
// own frame (max-w-lg), so on an iPad or a laptop the test sits in the same column as the rest
// of the app with the frame colour either side.
// The keyboard components find the dock through DockContext and portal their keys into it;
// their answer readout stays in the body. Intro and results screens stay inline as before.
const DockContext = React.createContext(null);

export function TestOverlay({ onClose, focused = false, children }) {
  // LB-315 (Alan, 17 Sep): back in the middle of a RUNNING test asks "Leave the test?" before
  // quitting — a swipe is not the deliberate act a tap on Quit is. Intro and results screens
  // close on back straight away, like their own Back to games. The overlay is one entry on
  // the shared back stack for as long as it is mounted.
  const [leaveAsk, setLeaveAsk] = React.useState(false);
  useBackLayer(true, onClose, () => { if (focused) { setLeaveAsk(true); return false; } return undefined; });
  React.useEffect(() => { if (!focused) setLeaveAsk(false); }, [focused]);
  const leaveStrip = leaveAsk ? (
    <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-slate-900 text-white" data-testid="test-leave-ask">
      <span className="text-sm font-bold flex-1">{ct("tests.leave_title", "Leave the test?")}</span>
      <button type="button" className="min-h-[40px] px-3 rounded-lg bg-white/15 text-sm font-bold" onClick={() => setLeaveAsk(false)} data-testid="test-leave-no">{ct("tests.leave_no", "Keep going")}</button>
      <button type="button" className="min-h-[40px] px-3 rounded-lg bg-white text-slate-900 text-sm font-bold" onClick={onClose} data-testid="test-leave-yes">{ct("tests.leave_yes", "Leave")}</button>
    </div>
  ) : null;
  // LB-19 Zip B v2 (Alan, staging pass: "games are still launching on a new page, not
  // in-line as agreed"): the intro and results render in the Learn tab's own flow. LB-368
  // (23 Sep) takes the RUNNING test full screen again, by his decision, for the keyboard.
  const inlineRef = React.useRef(null);
  const [dockEl, setDockEl] = React.useState(null);
  React.useEffect(() => {
    if (focused) return undefined;
    const t = setTimeout(() => { inlineRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 60);
    return () => clearTimeout(t);
  }, [focused]);
  React.useEffect(() => {
    if (!focused) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [focused]);
  if (focused) {
    return (
      <div className="lb-test-focus fixed inset-0 z-[70]" data-no-pull-refresh="" data-testid="test-focused">
        <div className="lb-test-column max-w-lg mx-auto h-full bg-white flex flex-col" data-testid="test-inline">
          <div className="flex-shrink-0 flex items-center px-2 border-b border-slate-100" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
            <button onClick={onClose} className="min-h-[44px] px-2 text-sm font-bold text-slate-500 flex items-center gap-1.5" data-testid="test-back">
              <Icon id="ic-close" size={16} style={{ color: "currentColor" }} />
              {ct("tests.quit", "Quit")}
            </button>
          </div>
          {leaveStrip}
          <div className="flex-1 overflow-y-auto px-3 pt-3 pb-4" style={{ overscrollBehavior: "contain" }}>
            <DockContext.Provider value={dockEl}>{children}</DockContext.Provider>
          </div>
          <div ref={setDockEl} className="lb-dock flex-shrink-0" data-testid="test-dock" />
        </div>
      </div>
    );
  }
  return (
    <div ref={inlineRef} className="relative bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid="test-inline" style={{ scrollMarginTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
      <div className="px-2 pt-2 pb-2">
        <button onClick={onClose} className="text-xs font-bold text-slate-500 flex items-center" data-testid="test-back">
          <Icon id="ic-back" size={12} className="inline-block align-[-1px] mr-1" style={{ color: "currentColor" }} />
          {ct("tests.back_to_games", "Back to games")}
        </button>
      </div>
      <div className="px-2 pb-4">
        {children}
      </div>
    </div>
  );
}

// Keys go to the dock when a focused test provides one, else render in place.
function Docked({ children }) {
  const dockEl = React.useContext(DockContext);
  if (dockEl && typeof ReactDOM !== "undefined" && ReactDOM.createPortal) {
    return ReactDOM.createPortal(<div className="lb-dock-keys" data-testid="test-keys">{children}</div>, dockEl);
  }
  return children;
}

// A rough "this is probably a laptop/desktop, not a touchscreen" check — no touch capability
// at all is a reasonable proxy, and false negatives (a touch-capable laptop) just mean that
// device keeps using the on-screen keypad, which still works fine either way. On an actual
// desktop this only adds a keydown listener; it never focuses a real <input>, so it can't
// reintroduce the on-screen-keyboard viewport jump this component exists to avoid.
// Skip = "come back to it" (Alan, 12 Sep). A skipped item moves to the END of the running
// order rather than scoring; the child meets it again before the round is over. Second time
// round it is final and scores wrong, so a never-answered item still counts against them —
// which is what keeps the adaptive signal honest (skip everything hard and the engine would
// otherwise read an easy run as a strong one).
//
// Two deliberate exceptions:
//   - the LAST item in the order has nothing to come back after, so skipping it is final;
//   - TT Streaks does not defer at all — it is a streak game, and a free skip past the one
//     question you cannot do would be the single thing worth gaming.
//
// Returns the reordered list, or null when the skip should score wrong instead.
function deferToEnd(list, idx) {
  if (!Array.isArray(list) || idx >= list.length - 1) return null;
  const copy = [...list];
  const [item] = copy.splice(idx, 1);
  copy.push(item);
  return copy;
}

// Should this keydown be treated as the child answering?
//
// 12 Sep: this used to be a device sniff (no touch support = a desktop), which switched
// physical typing OFF on a touchscreen laptop, an iPad with a keyboard and a Chromebook —
// the machines a parent actually tests on. A key event is itself the proof that a keyboard
// exists, so the sniff is gone. What remains are the two guards that matter: never swallow
// a browser shortcut, and never steal keys from a real text field.
function isAnswerKey(e) {
  if (e.metaKey || e.ctrlKey || e.altKey) return false;
  const el = e.target;
  const tag = el && el.tagName ? el.tagName.toLowerCase() : "";
  if (tag === "input" || tag === "textarea" || tag === "select" || (el && el.isContentEditable)) return false;
  return true;
}

// Score glyph (design pass B, Sep 2026): a sprite icon rather than an emoji — trophy at full
// marks, celebration at 80+, medal at 50+, reassurance below. Same thresholds as before.
export function scoreEmoji(pct) {
  const [id, hue] = pct >= 100 ? ["ic-trophy", "gold"] : pct >= 80 ? ["ic-celebrate", "pink"] : pct >= 50 ? ["ic-medal", "gold"] : ["ic-reassure", "indigo"];
  return <Icon id={id} size={28} hue={hue} className="inline-block align-[-5px] mr-1" />;
}

// pickEnglishVoice() moved to src/lib/speech.js in T1 (TTS_SCOPE.md §2.3). Everything that
// makes sound from words now goes through that module — nothing in this file touches
// window.speechSynthesis directly any more.


/* ------------------------------ Oinks (shared by all four games) --------------------------
 * Oinks (see lib/oinks.js) are the fun-only currency: +1 per correct answer, +5 for finishing,
 * +10 for a perfect session — paid ALWAYS, on cooldown weeks and cap-blocked weeks alike.
 * Each game awards exactly once, inside its existing `phase === "finished" && !awarded`
 * effect (the same single award point its token payout already uses), via the onEarnOinks
 * prop. Quitting mid-test forfeits the run's OP — one award point per session, no partial
 * credit. Both pieces below render only when oinksOn (the per-child oinksEnabled admin
 * toggle) is true, so switching Oinks off removes every trace from the games too.
 * -------------------------------------------------------------------------------------- */

// The emerald reward row on every results screen — same card shape as OinkRewardRow just
// below, so the token payout finally reads as a moment rather than a plain sentence (the
// Oink line always had this treatment; the token line hadn't — staging feedback, Aug 2026).
// Emerald/gold, never pink — keeps the two currencies visually distinct per Law 1.
export function TokenRewardRow({ amount, detail }) {
  if (!amount) return null;
  return (
    <div data-testid="token-reward-row" className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 mb-4 text-left">
      <CoinIcon className="w-7 h-7 flex-shrink-0" />
      <div className="min-w-0">
        <div className="font-bold text-sm text-emerald-600">+{amount} tokens</div>
        <div className="text-xs text-emerald-500 font-semibold">{detail}</div>
      </div>
    </div>
  );
}

// The pink reward row on every results screen — visually parallel to (but unmistakably
// distinct from) the token messaging above it: pink and square-badged, never gold.
export function OinkRewardRow({ op, detail }) {
  if (!op) return null;
  return (
    <div data-testid="oink-reward-row" className="flex items-center gap-2.5 bg-pink-50 border border-pink-200 rounded-xl px-3 py-2 mb-4 text-left">
      <OinkIcon className="w-7 h-7 flex-shrink-0" />
      <div className="min-w-0">
        <div className="font-bold text-sm text-pink-600">+{op} Oinks</div>
        <div className="text-xs text-pink-400 font-semibold">{detail}</div>
      </div>
    </div>
  );
}

// One line on every intro screen so a kid knows the practice pays either way — with the
// emphasis flipped up when the money side is paused (cooldown or weekly cap), since that's
// exactly the moment this message matters most.
function OinkIntroLine({ moneyPaused }) {
  return (
    <p data-testid="oink-intro-line" className="text-sm font-bold text-pink-500 -mt-2 mb-4">
      <OinkIcon className="w-4 h-4 inline-block align-[-3px] mr-1" />{moneyPaused ? "But Oinks never stop — every correct answer still earns them." : `Plus +${OINK_CORRECT_OP} Oink for every correct answer.`}
    </p>
  );
}

// LB-290 — the spelling test's progress row. Four marks, no words: this is a spelling test and
// anything that spells the answer defeats it.
//
// Wraps rather than shrinks (Alan, 15 Sep). A skip appends a slot, so a long test with several
// skips can exceed one line, and a second line is honest where 4px dots are a smudge.
export function ProgressDots({ slots }) {
  if (!slots || slots.length < 2) return null;
  return (
    <div className="flex flex-wrap gap-1 items-center mb-3" data-testid="test-progress" aria-hidden="true">
      {slots.map((s, i) => {
        if (s === "skipped") {
          return (
            <span key={i} data-slot="skipped" className="w-[11px] h-[11px] rounded-full border-[1.5px] border-slate-300 flex items-center justify-center flex-shrink-0">
              <span className="block w-[5px] h-[1.5px] rounded-sm bg-slate-300" />
            </span>
          );
        }
        // Alan, 16 Sep: wrong answers are GREY, not red — red mid-test is brutal for a child —
        // but distinguishable from still-to-come, which is the lighter grey. Right is teal.
        const tone = s === "correct" ? "bg-teal-600" : s === "wrong" ? "bg-slate-500" : s === "requeued" ? "bg-orange-500" : "bg-slate-300";
        return <span key={i} data-slot={s} className={`w-[7px] h-[7px] rounded-full flex-shrink-0 ${tone}`} />;
      })}
    </div>
  );
}

export { isAnswerKey, Docked, DockContext, deferToEnd, OinkIntroLine, answersMatch };
